import React, { FormEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  Building2,
  CalendarDays,
  CircleX,
  CloudSun,
  Hotel,
  ListOrdered,
  Loader2,
  MapPin,
  Route,
  Sparkles,
  Star,
  TriangleAlert,
  X,
} from "lucide-react";
import Navbar from "../SharedComponents/Navbar";
import Footer from "../SharedComponents/Footer";
import NoImg from "../assets/images/no-image-icon-23485.png";
import {
  CategoryOption,
  CityOption,
  fetchPlannerCategories,
  fetchPlannerCities,
  fetchPlannerHotels,
  fetchPlannerPlaceCardDetails,
  generatePlan,
  HotelOption,
  PlannerAlternative,
  PlannerApiError,
  PlannerDay,
  PlannerMode,
  PlannerPace,
  PlannerPlace,
  PlannerPlaceCardDetails,
  WeatherMode,
} from "../services/plannerService";
import ReactMarkdown from "react-markdown";
import WeatherIcon from "../SharedComponents/WeatherIcon";

type ValidationErrors = Record<string, string>;

const paceOptions: Array<{ value: PlannerPace; label: string; hint: string }> = [
  { value: "relaxed", label: "آرام", hint: "فاصله‌های کوتاه‌تر" },
  { value: "normal", label: "معمولی", hint: "انتخاب متعادل" },
  { value: "packed", label: "فشرده", hint: "مکان‌های بیشتر" },
];

const formatPlanDate = (date?: string) => {
  if (!date) return null;
  return new Intl.DateTimeFormat("fa-IR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(`${date}T12:00:00`));
};

const numberText = (value: number | null | undefined, digits = 1) =>
  typeof value === "number" && Number.isFinite(value) ? value.toFixed(digits) : "—";

const fieldErrorText = (value: unknown): string => {
  if (Array.isArray(value)) return value.map(String).join("، ");
  if (typeof value === "string") return value;
  return "مقدار واردشده معتبر نیست.";
};

const placeDetailsCache = new Map<string, PlannerPlaceCardDetails>();

const PlaceSummary: React.FC<{ place: PlannerPlace; badge?: string }> = ({ place, badge }) => {
  const [details, setDetails] = useState<PlannerPlaceCardDetails>(
    () => placeDetailsCache.get(place.place_id) || {},
  );
  const [imageLoading, setImageLoading] = useState(!placeDetailsCache.has(place.place_id));

  useEffect(() => {
    const cached = placeDetailsCache.get(place.place_id);
    if (cached) {
      setDetails(cached);
      setImageLoading(false);
      return;
    }
    const controller = new AbortController();
    setImageLoading(true);
    fetchPlannerPlaceCardDetails(place.place_id, controller.signal)
      .then((result) => {
        placeDetailsCache.set(place.place_id, result);
        setDetails(result);
      })
      .catch((detailsError) => {
        if (detailsError.name !== "AbortError") setDetails({});
      })
      .finally(() => setImageLoading(false));
    return () => controller.abort();
  }, [place.place_id]);

  return (
    <Link
      to={`/places/${place.place_id}`}
      className="group flex min-h-32 gap-3 rounded-xl border border-gray-200 bg-white p-3 transition hover:border-[#038545]/40 hover:shadow-md sm:min-h-36 sm:gap-4"
    >
      <div className="relative h-28 w-28 shrink-0 overflow-hidden rounded-lg bg-gray-100 sm:h-32 sm:w-32 sm:rounded-xl">
        {imageLoading ? (
          <div className="h-full w-full animate-pulse bg-gray-200" />
        ) : (
          <img
            src={details.imageUrl || NoImg}
            alt={place.name}
            onError={(event) => {
              event.currentTarget.src = NoImg;
            }}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-center py-1">
        {badge && <span className="mb-1 line-clamp-1 text-[10px] text-[#038545] sm:text-[11px]">{badge}</span>}
        <div className="flex items-start justify-between gap-2">
          <h4 className="line-clamp-1 text-sm font-myYekanFaNumDemiBold text-gray-900 group-hover:text-[#068646] sm:text-base">
            {place.name}
          </h4>
          {typeof place.rate === "number" && place.rate > 0 && (
            <span className="flex shrink-0 items-center gap-1 rounded-full bg-[#f3fefa] px-2 py-1 text-xs text-[#068646] font-myYekanFaNumDemiBold">
              {numberText(place.rate)} <Star size={12} fill="currentColor" />
            </span>
          )}
        </div>

        {(details.city || place.address) && (
          <div className="mt-2 flex items-center justify-between gap-2 text-xs text-black">
            <div className="flex min-w-0 items-center">
              <MapPin size={14} className="ml-1 shrink-0 text-gray-700" />
              <span className="line-clamp-1 font-myYekanFaNumMedium">{details.city || place.address}</span>
            </div>
            {!!details.reviewCount && (
              <span className="shrink-0 text-gray-500 font-myYekanFaNumRegular">({details.reviewCount} نظر)</span>
            )}
          </div>
        )}

        {details.description && (
          <p className="mt-2 line-clamp-2 text-justify text-[11px] leading-5 text-gray-600 font-myYekanRegular sm:text-xs">

            <ReactMarkdown>{details.description}</ReactMarkdown>
          </p>
        )}

        {!details.description && !!place.categories?.length && (
          <div className="mt-2 flex flex-wrap gap-1">
            {place.categories.slice(0, 2).map((category) => (
              <span key={category.id} className="rounded-full bg-gray-100 px-2 py-1 text-[10px] text-gray-600">
                {category.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
};

const PlannerDayWeather: React.FC<{ day: PlannerDay }> = ({ day }) => {
  const weather = day.weather;
  const warnings = day.weather_warnings || [];

  if (!weather && warnings.length === 0) return null;

  return (
    <section className="mb-4 rounded-xl border border-[#87e5b6] bg-[#f3fefa] p-3" aria-label={`آب‌وهوای روز ${day.day}`}>
      {weather && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <WeatherIcon condition={weather.condition} className="h-10 w-10" />
            <div>
              <p className="text-sm font-myYekanFaNumDemiBold text-gray-900">
                {weather.condition.description}
              </p>
              <p className="mt-0.5 text-xs text-gray-600">
                {numberText(weather.temperature_min_c)}° تا {numberText(weather.temperature_max_c)}°
              </p>
            </div>
          </div>
          {/* <a
            href={weather.attribution.url || "https://openweathermap.org/"}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] text-gray-500 underline-offset-2 hover:underline"
          >
            اطلاعات آب‌وهوا: OpenWeather
          </a> */}
        </div>
      )}
      {warnings.length > 0 && (
        <div className={`${weather ? "mt-3 border-t border-[#97d2b5] pt-3" : ""} space-y-1.5`}>
          {warnings.map((warning, index) => (
            <p key={`${warning}-${index}`} className="flex items-start gap-1.5 text-xs leading-5 text-amber-900">
              <TriangleAlert size={14} className="mt-0.5 shrink-0 text-amber-600" />
              {warning}
            </p>
          ))}
        </div>
      )}
    </section>
  );
};

const PlannerResults: React.FC<{
  alternatives: PlannerAlternative[];
  activeIndex: number;
  onChange: (index: number) => void;
}> = ({ alternatives, activeIndex, onChange }) => {
  const active = alternatives[activeIndex];
  if (!active) return null;

  return (
    <section className="mt-10 scroll-mt-6" id="planner-results">
      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <span className="text-xs text-[#038545]">پیشنهادهای لوتکا</span>
          <h2 className="mt-1 text-xl font-myYekanFaNumDemiBold text-gray-900">برنامه سفر شما</h2>
        </div>
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="برنامه‌های جایگزین">
          {alternatives.map((alternative, index) => (
            <button
              key={alternative.index}
              type="button"
              role="tab"
              aria-selected={activeIndex === index}
              onClick={() => onChange(index)}
              className={`rounded-full px-4 py-2 text-xs transition ${activeIndex === index
                  ? "bg-[#038545] text-white"
                  : "border border-gray-200 bg-white text-gray-700 hover:border-[#038545]"
                }`}
            >
              پیشنهاد {alternative.index}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 rounded-2xl bg-[#f3fefa] p-4 sm:max-w-md">
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <Route size={18} className="text-[#038545]" />
          <span>{numberText(active.total_distance_km)} کیلومتر تقریبی</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <CalendarDays size={18} className="text-[#038545]" />
          <span>{active.days.length} روز برنامه</span>
        </div>
      </div>

      {active.days.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-600">
          برای این انتخاب برنامه‌ای ساخته نشد. امتیاز حداقل یا تعداد روزها را کاهش دهید و دوباره تلاش کنید.
        </div>
      ) : (
        <div className="space-y-5">
          {active.days.map((day) => (
            <article key={day.day} className="overflow-hidden rounded-2xl border border-gray-200 bg-[#fafafa]">
              <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white px-5 py-4">
                <div>
                  <h3 className="font-myYekanFaNumDemiBold text-gray-900">روز {day.day}</h3>
                  {day.date && <span className="mt-1 block text-[10px] text-gray-500">{formatPlanDate(day.date)}</span>}
                </div>
                <div className="flex flex-wrap items-center justify-end gap-3">
                  <span className="text-xs text-gray-500">{numberText(day.total_distance_km)} کیلومتر تقریبی</span>
                </div>
              </header>
              <div className="p-4 sm:p-5">
                <PlannerDayWeather day={day} />
                {day.hotel && (
                  <div className="mb-4">
                    <div className="mb-2 flex items-center gap-2 text-xs text-gray-600">
                      <Hotel size={16} className="text-[#038545]" /> اقامتگاه پایه
                    </div>
                    <PlaceSummary place={day.hotel} />
                  </div>
                )}

                {day.stops?.length ? (
                  <ol className="space-y-3">
                    {[...day.stops]
                      .sort((a, b) => a.order - b.order)
                      .map((stop) => (
                        <li key={`${stop.order}-${stop.place.place_id}`} className="relative pr-9">
                          <span className="absolute right-0 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-[#038545] text-xs text-white">
                            {stop.order}
                          </span>
                          <PlaceSummary
                            place={stop.place}
                            badge={`${stop.label}${stop.distance_from_prev_km > 0
                                ? ` • ${numberText(stop.distance_from_prev_km)} کیلومتر از توقف قبل`
                                : ""
                              }`}
                          />
                        </li>
                      ))}
                  </ol>
                ) : (
                  <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
                    مکان کافی برای تکمیل این روز پیدا نشد. امتیاز حداقل را کاهش دهید یا تنظیمات دیگری انتخاب کنید.
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      <p className="mt-4 text-xs leading-6 text-gray-500">
        این برنامه یک پیشنهاد است. فاصله‌ها مستقیم و تقریبی‌اند و ساعت کاری، ترافیک، زمان ماندن و رزرو را تضمین نمی‌کند.
      </p>
    </section>
  );
};

const WizardPage: React.FC = () => {
  const [mode, setMode] = useState<PlannerMode>("hotel");
  const [cities, setCities] = useState<CityOption[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [hotels, setHotels] = useState<HotelOption[]>([]);
  const [city, setCity] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [hotelId, setHotelId] = useState("");
  const [days, setDays] = useState(2);
  const [alternativesCount, setAlternativesCount] = useState(2);
  const [pace, setPace] = useState<PlannerPace>("normal");
  const [minRating, setMinRating] = useState(2.5);
  const [startDate, setStartDate] = useState("");
  const [weatherMode, setWeatherMode] = useState<WeatherMode>("off");
  const [useLocation, setUseLocation] = useState(false);
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [maxDistance, setMaxDistance] = useState("");
  const [lookupLoading, setLookupLoading] = useState(true);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [alternatives, setAlternatives] = useState<PlannerAlternative[]>([]);
  const [activeAlternative, setActiveAlternative] = useState(0);
  const requestController = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([fetchPlannerCities(controller.signal), fetchPlannerCategories(controller.signal)])
      .then(([cityData, categoryData]) => {
        setCities(cityData);
        setCategories(categoryData);
      })
      .catch((lookupError) => {
        if (lookupError.name !== "AbortError") setError(lookupError.message || "دریافت اطلاعات اولیه ناموفق بود.");
      })
      .finally(() => setLookupLoading(false));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    setHotelId("");
    setHotels([]);
    if (mode !== "hotel" || !city) return;
    const controller = new AbortController();
    setHotelsLoading(true);
    fetchPlannerHotels(city, controller.signal)
      .then(setHotels)
      .catch((hotelError) => {
        if (hotelError.name !== "AbortError") setError(hotelError.message || "دریافت اقامتگاه‌ها ناموفق بود.");
      })
      .finally(() => setHotelsLoading(false));
    return () => controller.abort();
  }, [city, mode]);

  useEffect(() => () => requestController.current?.abort(), []);

  const addCategory = (name: string) => {
    if (selectedCategories.length >= 10) {
      setValidationErrors((current) => ({ ...current, categories: "حداکثر ۱۰ دسته‌بندی قابل انتخاب است." }));
      return;
    }
    setSelectedCategories((current) => [...current, name]);
    setValidationErrors((current) => ({ ...current, categories: "" }));
  };

  const moveCategory = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= selectedCategories.length) return;
    setSelectedCategories((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const validate = (): ValidationErrors => {
    const next: ValidationErrors = {};
    if (!city) next.city = "یک شهر انتخاب کنید.";
    if (mode === "category" && (selectedCategories.length < 1 || selectedCategories.length > 10)) {
      next.categories = "بین ۱ تا ۱۰ دسته‌بندی انتخاب کنید.";
    }
    if (!Number.isInteger(days) || days < 1 || days > 14) next.days = "تعداد روز باید بین ۱ تا ۱۴ باشد.";
    if (!Number.isInteger(alternativesCount) || alternativesCount < 1 || alternativesCount > 5) {
      next.num_alternatives = "تعداد پیشنهادها باید بین ۱ تا ۵ باشد.";
    }
    if (minRating < 0 || minRating > 5) next.min_rating = "امتیاز باید بین ۰ تا ۵ باشد.";
    if (useLocation) {
      const lat = Number(latitude);
      const lon = Number(longitude);
      if (!latitude || !longitude || !Number.isFinite(lat) || !Number.isFinite(lon)) {
        next.location = "عرض و طول جغرافیایی را با هم وارد کنید.";
      } else if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
        next.location = "مختصات واردشده معتبر نیست.";
      }
      if (maxDistance && Number(maxDistance) < 0.5) next.max_distance = "شعاع باید حداقل ۰٫۵ کیلومتر باشد.";
    }
    return next;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const errors = validate();
    setValidationErrors(errors);
    setError("");
    if (Object.keys(errors).length) return;

    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    setLoading(true);
    setAlternatives([]);
    setActiveAlternative(0);

    const common = {
      city,
      days,
      num_alternatives: alternativesCount,
      pace,
      min_rating: minRating,
      weather_mode: startDate ? weatherMode : "off",
      ...(startDate ? { start_date: startDate } : {}),
      ...(useLocation
        ? {
          user_lat: Number(latitude),
          user_lon: Number(longitude),
          ...(maxDistance ? { max_distance: Number(maxDistance) } : {}),
        }
        : {}),
    };

    try {
      const result = await generatePlan(
        mode,
        mode === "hotel"
          ? { ...common, hotel_place_id: hotelId || null }
          : { ...common, categories: selectedCategories },
        controller.signal,
      );
      setAlternatives(result);
      setActiveAlternative(0);
      if (!result.length) setError("برای این تنظیمات پیشنهادی پیدا نشد. تنظیمات را تغییر دهید و دوباره تلاش کنید.");
      setTimeout(() => document.getElementById("planner-results")?.scrollIntoView({ behavior: "smooth" }), 0);
    } catch (submitError) {
      if ((submitError as Error).name === "AbortError") return;
      if (submitError instanceof PlannerApiError && submitError.fields) {
        const apiFields: ValidationErrors = {};
        Object.entries(submitError.fields).forEach(([key, value]) => {
          apiFields[key] = fieldErrorText(value);
        });
        setValidationErrors(apiFields);
      }
      setError(
        submitError instanceof PlannerApiError
          ? submitError.message
          : "ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی و دوباره تلاش کنید.",
      );
    } finally {
      if (requestController.current === controller) {
        setLoading(false);
        requestController.current = null;
      }
    }
  };

  const cancelRequest = () => requestController.current?.abort();

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="site-container pb-8 pt-20 font-myYekanFaNumRegular sm:pb-12 tablet:py-12">
        <div className="mx-auto max-w-5xl">
          <header className="mb-8 text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#f3fefa] px-3 py-1.5 text-xs text-[#068646]">
              <Sparkles size={14} /> برنامه‌ریز سفر لوتکا
            </span>
            <h1 className="mt-4 text-2xl font-myYekanFaNumDemiBold text-gray-950 sm:text-3xl">سفر خودت رو بساز</h1>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-gray-600">
              شهر و سبک برنامه را انتخاب کن تا چند پیشنهاد روزبه‌روز بر اساس مکان‌های لوتکا دریافت کنی.
            </p>
          </header>

          <form onSubmit={handleSubmit} className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm sm:p-7">
            <fieldset>
              <legend className="mb-3 text-sm font-myYekanFaNumDemiBold text-gray-900">نوع برنامه</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setMode("hotel")}
                  className={`flex items-start gap-3 rounded-2xl border p-4 text-right transition ${mode === "hotel" ? "border-[#038545] bg-[#f3fefa]" : "border-gray-200 hover:border-gray-300"
                    }`}
                >
                  <Building2 className="mt-0.5 shrink-0 text-[#038545]" size={22} />
                  <span><b className="block text-sm">بر پایه اقامتگاه</b><small className="mt-1 block leading-5 text-gray-500">هر روز از یک اقامتگاه پایه شروع می‌شود.</small></span>
                </button>
                <button
                  type="button"
                  onClick={() => setMode("category")}
                  className={`flex items-start gap-3 rounded-2xl border p-4 text-right transition ${mode === "category" ? "border-[#038545] bg-[#f3fefa]" : "border-gray-200 hover:border-gray-300"
                    }`}
                >
                  <ListOrdered className="mt-0.5 shrink-0 text-[#038545]" size={22} />
                  <span><b className="block text-sm">بر پایه علاقه‌مندی</b><small className="mt-1 block leading-5 text-gray-500">ترتیب دسته‌ها، ترتیب توقف‌های هر روز است.</small></span>
                </button>
              </div>
            </fieldset>

            <div className="my-6 h-px bg-gray-100" />

            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block text-sm text-gray-800">
                <span className="mb-2 block font-myYekanFaNumDemiBold">شهر مقصد</span>
                <select
                  value={city}
                  onChange={(event) => setCity(event.target.value)}
                  disabled={lookupLoading}
                  className="h-12 w-full rounded-xl border border-gray-300 bg-white px-3 outline-none focus:border-[#038545] disabled:bg-gray-100"
                >
                  <option value="">{lookupLoading ? "در حال دریافت شهرها..." : "انتخاب شهر"}</option>
                  {cities.map((option) => <option key={option.id} value={option.name}>{option.name}</option>)}
                </select>
                {validationErrors.city && <small className="mt-1 block text-red-600">{validationErrors.city}</small>}
              </label>

              {mode === "hotel" && (
                <label className="block text-sm text-gray-800">
                  <span className="mb-2 block font-myYekanFaNumDemiBold">اقامتگاه دلخواه <small className="font-normal text-gray-400">(اختیاری)</small></span>
                  <select
                    value={hotelId}
                    onChange={(event) => setHotelId(event.target.value)}
                    disabled={!city || hotelsLoading}
                    className="h-12 w-full rounded-xl border border-gray-300 bg-white px-3 outline-none focus:border-[#038545] disabled:bg-gray-100"
                  >
                    <option value="">{hotelsLoading ? "در حال دریافت اقامتگاه‌ها..." : "انتخاب خودکار"}</option>
                    {hotels.map((hotel) => <option key={hotel.place_id} value={hotel.place_id}>{hotel.name}</option>)}
                  </select>
                </label>
              )}
            </div>

            {mode === "category" && (
              <section className="mt-6">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <h2 className="text-sm font-myYekanFaNumDemiBold text-gray-800">دسته‌بندی‌ها و ترتیب توقف‌ها</h2>
                  <span className="text-xs text-gray-400">{selectedCategories.length}/۱۰</span>
                </div>
                <p className="mb-3 text-xs leading-6 text-gray-500">برای افزودن دوباره یک دسته روی آن کلیک کن؛ ترتیب انتخاب برای هر روز تکرار می‌شود.</p>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => addCategory(category.name)}
                      className="rounded-full border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 transition hover:border-[#038545] hover:bg-[#f3fefa]"
                    >
                      + {category.name}
                    </button>
                  ))}
                </div>
                {selectedCategories.length > 0 && (
                  <ol className="mt-4 space-y-2 rounded-2xl bg-gray-50 p-3">
                    {selectedCategories.map((name, index) => (
                      <li key={`${name}-${index}`} className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white p-2 text-sm">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#f3fefa] text-xs text-[#068646]">{index + 1}</span>
                        <span className="min-w-0 flex-1 truncate">{name}</span>
                        <button type="button" aria-label="انتقال به بالا" onClick={() => moveCategory(index, -1)} disabled={index === 0} className="p-1 text-gray-500 disabled:opacity-25"><ArrowUp size={16} /></button>
                        <button type="button" aria-label="انتقال به پایین" onClick={() => moveCategory(index, 1)} disabled={index === selectedCategories.length - 1} className="p-1 text-gray-500 disabled:opacity-25"><ArrowDown size={16} /></button>
                        <button type="button" aria-label="حذف" onClick={() => setSelectedCategories((current) => current.filter((_, itemIndex) => itemIndex !== index))} className="p-1 text-red-500"><X size={16} /></button>
                      </li>
                    ))}
                  </ol>
                )}
                {validationErrors.categories && <small className="mt-2 block text-red-600">{validationErrors.categories}</small>}
              </section>
            )}

            <div className="my-6 h-px bg-gray-100" />

            <div className="grid gap-5 sm:grid-cols-3">
              <label className="text-sm text-gray-800">
                <span className="mb-2 block font-myYekanFaNumDemiBold">تعداد روز</span>
                <input type="number" min={1} max={14} value={days} onChange={(event) => setDays(Number(event.target.value))} className="h-12 w-full rounded-xl border border-gray-300 px-3 outline-none focus:border-[#038545]" />
                {validationErrors.days && <small className="mt-1 block text-red-600">{validationErrors.days}</small>}
              </label>
              <label className="text-sm text-gray-800">
                <span className="mb-2 block font-myYekanFaNumDemiBold">تعداد پیشنهاد</span>
                <input type="number" min={1} max={5} value={alternativesCount} onChange={(event) => setAlternativesCount(Number(event.target.value))} className="h-12 w-full rounded-xl border border-gray-300 px-3 outline-none focus:border-[#038545]" />
                {validationErrors.num_alternatives && <small className="mt-1 block text-red-600">{validationErrors.num_alternatives}</small>}
              </label>
              <label className="text-sm text-gray-800">
                <span className="mb-2 block font-myYekanFaNumDemiBold">حداقل امتیاز</span>
                <input type="number" min={0} max={5} step={0.5} value={minRating} onChange={(event) => setMinRating(Number(event.target.value))} className="h-12 w-full rounded-xl border border-gray-300 px-3 outline-none focus:border-[#038545]" />
                {validationErrors.min_rating && <small className="mt-1 block text-red-600">{validationErrors.min_rating}</small>}
              </label>
            </div>

            <fieldset className="mt-6">
              <legend className="mb-3 text-sm font-myYekanFaNumDemiBold text-gray-800">ریتم سفر</legend>
              <div className="grid grid-cols-3 gap-2">
                {paceOptions.map((option) => (
                  <button key={option.value} type="button" onClick={() => setPace(option.value)} className={`rounded-xl border px-2 py-3 text-center transition ${pace === option.value ? "border-[#038545] bg-[#f3fefa] text-[#068646]" : "border-gray-200 text-gray-700"}`}>
                    <span className="block text-sm font-myYekanFaNumDemiBold">{option.label}</span>
                    <small className="mt-1 hidden text-[10px] text-gray-500 sm:block">{option.hint}</small>
                  </button>
                ))}
              </div>
            </fieldset>

            <section className="mt-6 rounded-2xl border border-gray-200 p-4">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#f3fefa] text-[#038545]">
                  <CloudSun size={19} />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="font-myYekanFaNumDemiBold text-sm text-gray-900">آب‌وهوای سفر</h2>
                  <p className="mt-1 text-xs leading-5 text-gray-500">با انتخاب تاریخ، وضعیت آب‌وهوا را در برنامه ببینید یا برنامه را با آن تطبیق دهید.</p>
                </div>
              </div>

              <label className="mt-4 block text-sm text-gray-800">
                <span className="mb-2 block font-myYekanFaNumMedium">تاریخ شروع سفر <small className="font-normal text-gray-400">(اختیاری)</small></span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => {
                    const nextDate = event.target.value;
                    setStartDate(nextDate);
                    if (!nextDate) setWeatherMode("off");
                  }}
                  className="h-12 w-full rounded-xl border border-gray-300 bg-white px-3 outline-none focus:border-[#038545] sm:max-w-xs"
                />
              </label>

              {!startDate ? (
                <p role="status" className="mt-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-3 py-2.5 text-xs leading-6 text-gray-600">
                  بدون تعیین تاریخ، شرایط آب‌وهوا در برنامه‌ریزی در نظر گرفته نمی‌شود.
                </p>
              ) : (
                <fieldset className="mt-4">
                  <legend className="mb-2 text-xs font-myYekanFaNumMedium text-gray-800">پشتیبانی آب‌وهوا</legend>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {[
                      { value: "off" as const, label: "غیرفعال", hint: "برنامهٔ معمولی" },
                      { value: "advisory" as const, label: "نمایش اطلاعات", hint: "پیش‌بینی و هشدارها" },
                      { value: "adaptive" as const, label: "تطبیق با آب‌وهوا", hint: "اولویت‌دهی هوشمند" },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={`cursor-pointer rounded-xl border p-3 text-right transition ${weatherMode === option.value ? "border-[#038545] bg-[#f3fefa]" : "border-gray-200 bg-white hover:border-gray-300"}`}
                      >
                        <input
                          type="radio"
                          name="weather-mode"
                          value={option.value}
                          checked={weatherMode === option.value}
                          onChange={() => setWeatherMode(option.value)}
                          className="sr-only"
                        />
                        <span className="block text-xs font-myYekanFaNumDemiBold text-gray-900">{option.label}</span>
                        <small className="mt-1 block text-[10px] leading-5 text-gray-500">{option.hint}</small>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
            </section>

            <section className="mt-6 rounded-2xl border border-gray-200 p-4">
              <label className="flex cursor-pointer items-center justify-between gap-3">
                <span><b className="block text-sm text-gray-800">محدوده جغرافیایی</b><small className="mt-1 block text-xs leading-5 text-gray-500"> برای پیشنهاد مکان‌های نزدیک‌تر به یک نقطه</small></span>
                <input type="checkbox" checked={useLocation} onChange={(event) => setUseLocation(event.target.checked)} className="h-5 w-5 accent-[#038545]" />
              </label>
              {useLocation && (
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <label className="text-xs text-gray-600">عرض جغرافیایی<input type="number" step="any" value={latitude} onChange={(event) => setLatitude(event.target.value)} className="mt-1 h-11 w-full rounded-xl border border-gray-300 px-3 text-sm outline-none focus:border-[#038545]" /></label>
                  <label className="text-xs text-gray-600">طول جغرافیایی<input type="number" step="any" value={longitude} onChange={(event) => setLongitude(event.target.value)} className="mt-1 h-11 w-full rounded-xl border border-gray-300 px-3 text-sm outline-none focus:border-[#038545]" /></label>
                  <label className="text-xs text-gray-600">شعاع (کیلومتر)<input type="number" min={0.5} step={0.5} value={maxDistance} onChange={(event) => setMaxDistance(event.target.value)} className="mt-1 h-11 w-full rounded-xl border border-gray-300 px-3 text-sm outline-none focus:border-[#038545]" /></label>
                </div>
              )}
              {validationErrors.location && <small className="mt-2 block text-red-600">{validationErrors.location}</small>}
              {validationErrors.max_distance && <small className="mt-2 block text-red-600">{validationErrors.max_distance}</small>}
            </section>

            {error && (
              <div role="alert" className="mt-5 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm leading-6 text-red-700">
                <CircleX size={18} className="mt-0.5 shrink-0" /> {error}
              </div>
            )}

            <div className="mt-6 flex flex-col gap-2 sm:flex-row">
              <button type="submit" disabled={loading || lookupLoading} className="flex h-12 min-h-[48px] w-full flex-none shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-[#038545] px-5 text-sm text-white transition hover:bg-[#068646] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:flex-1">
                {loading ? <><Loader2 size={18} className="animate-spin" /> در حال ساخت برنامه...</> : <><Sparkles size={18} /> ساخت برنامه سفر</>}
              </button>
              {loading && <button type="button" onClick={cancelRequest} className="h-12 rounded-xl border border-gray-300 px-5 text-sm text-gray-700 hover:bg-gray-50">لغو درخواست</button>}
            </div>
          </form>

          {alternatives.length > 0 && <PlannerResults alternatives={alternatives} activeIndex={activeAlternative} onChange={setActiveAlternative} />}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WizardPage;
